import React, { useEffect, useState } from "react";
import { Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { Box, Button } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { FiSave } from "react-icons/fi";
import { addNewMessage, setIsLoading, setPageTitle } from "../slice";
import { PROFILES_API } from "../api";
import {
  useCurrentState,
  useEducationInfo,
  useFinancialInfo,
  usePersonalInfo,
  useFamilyInfo,
  useDemandsInfo,
  useDocumentInfo,
  useBasicInfo,
} from "../hooks";
import BasicInformation from "../components/form/BasicInformation";
import PersonalInfo from "../components/form/PersonalInfo";
import FamilyInformation from "../components/form/FamilyInformation";
import Education from "../components/form/Education";
import FinancialInfo from "../components/form/FinancialInfo";
import Requests from "../components/form/Requests";
import DocumentInformation from "../components/form/DocumentInformation";
import Stepper from "../components/Stepper";
import SuccessMessage from "../components/alerts/SuccessMessage";
import ErrorMessage from "../components/alerts/ErrorMessage";

function PersonalInformation() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const updateUserStatus = useCurrentState();
  const { data: userState } = useSelector((state) => state.UserCurrentState);
  const hasEditAccess =
    userState.code !== undefined && [0, 18, 24].includes(parseInt(userState.code));
  const [errors, setErrors] = useState(0);
  const [error_message, setError_message] = useState([]);
  const { handleSubmit: handleSubmitBasicForm } = useBasicInfo({ hasEditAccess, errors });
  const { handleSubmit: handleSubmitPersonalForm } = usePersonalInfo({ hasEditAccess });
  const { handleSubmit: handleSubmitEducationForm } = useEducationInfo({ hasEditAccess });
  const { handleSubmit: handleSubmitFinancialForm } = useFinancialInfo({ hasEditAccess });
  const { handleSubmit: handleSubmitFamilyForm } = useFamilyInfo({ hasEditAccess, errors });
  const { handleSubmit: handleSubmitDemandsForm } = useDemandsInfo({ hasEditAccess, errors });
  const handleSubmitDocumentsForm = useDocumentInfo({ hasEditAccess });
  const [mode_profile_for_approval, setMode_profile_for_approval] = useState("");
  const CURRENT_LOCATION = location.pathname.split("/").pop();

  const handleStepperStatus = (route, pageCode) => {
    if (CURRENT_LOCATION === route && mode_profile_for_approval === "") {
      return "active";
    } else {
      if (mode_profile_for_approval === "") {
        return "";
      } else if (mode_profile_for_approval === "error") {
        if (error_message.find((item) => item.pageCode === pageCode) !== undefined) {
          return "error";
        } else {
          return "complete";
        }
      }
    }
  };

  //! status 3 halate 'active' , 'complete' ,'error' dard
  const steps = [
    {
      route: "base-info",
      label: "اطلاعات پایه",
      disabled: false,
      status: handleStepperStatus("base-info", 1),
      errors:
        error_message.length === 0
          ? ""
          : error_message?.find((item) => item.pageCode === 1)?.errorMessages &&
            Object.keys(error_message?.find((item) => item.pageCode === 1)?.errorMessages)?.length
          ? `${
              Object.keys(error_message?.find((item) => item.pageCode === 1)?.errorMessages)?.length
            } خطا در این صفحه وجود دارد!`
          : "خطایی در این صفحه وجود ندارد!",
      pageErrors: error_message.find(({ pageCode }) => pageCode === 1)?.errorMessages,
      component: BasicInformation,
    },
    {
      route: "personal-info",
      label: "اطلاعات شخصی",
      disabled: false,
      status: handleStepperStatus("personal-info", 2),
      errors:
        error_message.length === 0
          ? ""
          : error_message?.find((item) => item.pageCode === 2)?.errorMessages &&
            Object.keys(error_message?.find((item) => item.pageCode === 2)?.errorMessages)?.length
          ? `${
              Object.keys(error_message?.find((item) => item.pageCode === 2)?.errorMessages)?.length
            } خطا در این صفحه وجود دارد!`
          : "خطایی در این صفحه وجود ندارد!",
      pageErrors: error_message.find(({ pageCode }) => pageCode === 2)?.errorMessages,
      component: PersonalInfo,
    },

    {
      route: "education",
      label: "اطلاعات تحصیلی",
      disabled: false,
      status: handleStepperStatus("education", 4),
      errors:
        error_message.length === 0
          ? ""
          : error_message?.find((item) => item.pageCode === 4)?.errorMessages &&
            Object.keys(error_message?.find((item) => item.pageCode === 4)?.errorMessages)?.length
          ? `${
              Object.keys(error_message?.find((item) => item.pageCode === 4)?.errorMessages)?.length
            } خطا در این صفحه وجود دارد!`
          : "خطایی در این صفحه وجود ندارد!",
      pageErrors: error_message.find(({ pageCode }) => pageCode === 4)?.errorMessages,
      component: Education,
    },
    {
      route: "financial-info",
      label: "اطلاعات اقتصادی",
      disabled: false,
      status: handleStepperStatus("financial-info", 5),
      errors:
        error_message.length === 0
          ? ""
          : error_message?.find((item) => item.pageCode === 5)?.errorMessages &&
            Object.keys(error_message?.find((item) => item.pageCode === 5)?.errorMessages)?.length
          ? `${
              Object.keys(error_message?.find((item) => item.pageCode === 5)?.errorMessages)?.length
            } خطا در این صفحه وجود دارد!`
          : "خطایی در این صفحه وجود ندارد!",
      pageErrors: error_message.find(({ pageCode }) => pageCode === 5)?.errorMessages,
      component: FinancialInfo,
    },
    {
      route: "family-info",
      label: "اطلاعات خانوادگی",
      disabled: false,
      status: handleStepperStatus("family-info", 3),
      errors:
        error_message.length === 0
          ? ""
          : error_message?.find((item) => item.pageCode === 3)?.errorMessages &&
            Object.keys(error_message?.find((item) => item.pageCode === 3)?.errorMessages)?.length
          ? `${
              Object.keys(error_message?.find((item) => item.pageCode === 3)?.errorMessages)?.length
            } خطا در این صفحه وجود دارد!`
          : "خطایی در این صفحه وجود ندارد!",
      pageErrors: error_message.find(({ pageCode }) => pageCode === 3)?.errorMessages,
      component: FamilyInformation,
    },
    {
      route: "request",
      label: "خواسته های من",
      disabled: false,
      status: handleStepperStatus("request", 6),
      errors:
        error_message.length === 0
          ? ""
          : error_message?.find((item) => item.pageCode === 6)?.errorMessages &&
            Object.keys(error_message?.find((item) => item.pageCode === 6)?.errorMessages)?.length
          ? `${
              Object.keys(error_message?.find((item) => item.pageCode === 6)?.errorMessages)?.length
            } خطا در این صفحه وجود دارد!`
          : "خطایی در این صفحه وجود ندارد!",
      pageErrors: error_message.find(({ pageCode }) => pageCode === 6)?.errorMessages,
      component: Requests,
    },
    {
      route: "document",
      label: "بارگذاری مدارک",
      disabled: false,
      status: handleStepperStatus("document", 7),
      errors:
        error_message.length === 0
          ? ""
          : error_message?.find((item) => item.pageCode === 7)?.errorMessages &&
            Object.keys(error_message?.find((item) => item.pageCode === 7)?.errorMessages)?.length
          ? `${
              Object.keys(error_message?.find((item) => item.pageCode === 7)?.errorMessages)?.length
            } خطا در این صفحه وجود دارد!`
          : "خطایی در این صفحه وجود ندارد!",
      pageErrors: error_message.find(({ pageCode }) => pageCode === 7)?.errorMessages,
      component: DocumentInformation,
    },
  ];

  const { mutateAsync: sendProfileForApproval } = useMutation(
    (data) => PROFILES_API.SendProfileForApproval(data),
    {
      onError: ({ response: { data } }) => {
        if (data) {
          if (data.code === 409) {
            dispatch(
              addNewMessage({
                text: data?.message,
                type: "error",
                id: "error-in-send-profile",
              })
            );
          } else {
            setError_message(data?.data);
            setMode_profile_for_approval("error");
          }
        }
      },
      onSuccess: ({ data }) => {
        if (data.success) {
          window.sessionStorage.setItem("approved_info", JSON.stringify(data));
          setMode_profile_for_approval("succes");
          navigate("/wait");
          updateUserStatus();
        }
        //todo: inja byd payam ha handle beshan
      },
    }
  );

  const countErrors = (errors) => {
    let result = 0;
    Object.keys(errors).forEach((item) => {
      if (errors[item].length !== 0) result += 1;
    });
    return result;
  };

  const sendForApprovalClickHandler = async (event) => {
    event.preventDefault();
    await saveInformation(CURRENT_LOCATION, null);
    if (userState.code !== undefined)
      if (["0", "18", "17", "24"].includes(userState.code.toString())) {
        if (countErrors(errors) === 0) await sendProfileForApproval();
      } else {
        dispatch(
          addNewMessage({
            id: "failed-on-use",
            text: "متقاضی گرامی در وضعیت کنونی شما امکان استفاده از این قابلیت برای شما فراهم نیست.",
            type: "error",
          })
        );
      }
  };

  //todo:این قسمت باید به سوییچ و کیس تبدیل بشه
  const saveInformation = async (route, next_route) => {
    if (route === "base-info") {
      await handleSubmitBasicForm(next_route);
    } else if (route === "personal-info") {
      await handleSubmitPersonalForm(next_route);
    } else if (route === "family-info") {
      await handleSubmitFamilyForm(next_route);
    } else if (route === "financial-info") {
      await handleSubmitFinancialForm(next_route);
    } else if (route === "education") {
      await handleSubmitEducationForm(next_route);
    } else if (route === "request") {
      await handleSubmitDemandsForm(next_route);
    } else if (route === "document") {
      await handleSubmitDocumentsForm(next_route);
    }
    if (route === next_route) {
      dispatch(setIsLoading(false));
    }
  };

  //todo: این تابع بعدا از اینکه کدنویس صفحه‌های اطلاعات تموم شد باید حذف بشه و با تابع استفاده شده جایگزین بشه
  const handleSendRequest = () => {
    if (hasEditAccess) {
      saveInformation(CURRENT_LOCATION, null);
    } else {
      dispatch(
        addNewMessage({
          id: "failed-on-use",
          text: "متقاضی گرامی در وضعیت کنونی شما امکان استفاده از این قابلیت برای شما فراهم نیست.",
          type: "error",
        })
      );
    }
  };

  useEffect(() => {
    dispatch(setPageTitle("اطلاعات من"));
  }, [dispatch]);

  return (
    <>
      <Box
        flexDirection={{ xs: "column-reverse", sm: "row", md: "row" }}
        justifyContent={{
          xs: "right",
          sm: "space-between",
          md: "space-between",
        }}
        width="100%"
      >
        {hasEditAccess && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent={{ xs: "center", sm: "left", md: "left" }}
            width={{ xs: "100%", sm: "65%", md: "60%" }}
            mt={{ xs: 2, sm: 0, md: 0 }}
            mb={3}
          >
            <Button
              variant="s1"
              sx={{
                marginRight: 2,
                fontSize: { xs: "10px", sm: "14px", md: "14px" },
              }}
              onClick={(event) => {
                // await saveInformation(CURRENT_LOCATION, "base-info");
                sendForApprovalClickHandler(event);
              }}
            >
              ارسال مدارک به مرکز
            </Button>
            <Button
              variant="s2"
              sx={{ fontSize: { xs: "10px", sm: "14px", md: "14px" } }}
              endIcon={<FiSave color="#c6c4d0" />}
              onClick={handleSendRequest}
            >
              ذخیره اطلاعات
            </Button>
          </Box>
        )}
        {mode_profile_for_approval === "error" && (
          <ErrorMessage
            message="متاسفانه به دلیل ناقص بودن مدارک ارسال مدارک به مرکز با مشکل مواجه شده است.(برای مشاهده جزییات بر روی ... کلیک نمایید.)"
            messages={error_message}
          />
        )}
        <SuccessMessage
          message="اطلاعات با موفقیت ذخیره شد."
          show={mode_profile_for_approval === "success"}
          onClose={() => {
            setMode_profile_for_approval("");
          }}
        />
      </Box>
      <Stepper steps={steps} onChange={({ route }) => saveInformation(CURRENT_LOCATION, route)} />
      <Routes>
        {steps.map((item) => (
          <Route
            key={item.route}
            path={`${item.route}`}
            element={
              <item.component
                hasEditAccess={hasEditAccess}
                onError={(errors) => {
                  setErrors(errors);
                }}
                pageErrors={item.pageErrors}
                disabled={!hasEditAccess}
              />
            }
          />
        ))}
      </Routes>
    </>
  );
}

export default PersonalInformation;
